import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Grid,
  Modal,
  Stack,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  Refresh,
  Search,
  ChevronLeft,
  ChevronRight,
  PendingActions,
  FileDownload,
  Settings,
  LocationOn
} from "@mui/icons-material";
import { CSVLink } from "react-csv";
import Page from "src/components/Page";
import CustomLoader from "src/components/CustomLoader";
import { getReferralMemberReport, getReferralWalletSetting, updateReferralWalletSetting } from "src/api/commonapi";
import { Routes } from "src/constant/api_url";
import { notification } from "src/utils/messages";

const formatCurrency = (amount) => {
  const num = parseFloat(amount);
  if (isNaN(num)) return "₹0";
  return `₹${num.toLocaleString("en-IN")}`;
};

const getInitials = (name) => {
  if (!name) return "?";
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

export default function ReferralReportList() {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state || {};

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimerRef = useRef(null);
  const [withdrawFilter, setWithdrawFilter] = useState(navState.w_status ?? 0);
  const [fromDate, setFromDate] = useState(navState.from_date ?? "");
  const [toDate, setToDate] = useState(navState.to_date ?? "");

  const [settingOpen, setSettingOpen] = useState(false);
  const [settingLoading, setSettingLoading] = useState(false);
  const [settingSaving, setSettingSaving] = useState(false);
  const [settingForm, setSettingForm] = useState({
    referral_user_amount: "",
    referral_min_w_amount: ""
  });

  useEffect(() => {
    if (location.state) {
      window.history.replaceState({}, "");
    }
  }, []);

  const withdrawOptions = [
    { label: "All", value: 0 },
    { label: "Pending Withdraw Request", value: 1 }
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await getReferralMemberReport({
        page,
        per_page: perPage,
        search: debouncedSearch,
        w_status: withdrawFilter,
        from_date: fromDate,
        to_date: toDate
      });
      if (resp.status == 1) {
        setData(resp.data || []);
        setTotalRecords(resp.total_records || 0);
      } else {
        setData([]);
        setTotalRecords(0);
      }
    } catch {
      setData([]);
      setTotalRecords(0);
    }
    setLoading(false);
  }, [page, perPage, withdrawFilter, debouncedSearch, fromDate, toDate]);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(searchTimerRef.current);
  }, [search]);

  useEffect(() => {
    fetchData();
  }, [fetchData, page, perPage]);

  const handleFilter = () => {
    setPage(1);
    setDebouncedSearch(search);
  };

  const handleClearFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setWithdrawFilter(0);
    setFromDate("");
    setToDate("");
    setPage(1);
  };

  const handleViewDetail = (mId) => {
    navigate(`/referral-report/${mId}`);
  };

  const handleOpenSetting = async () => {
    setSettingOpen(true);
    setSettingLoading(true);
    try {
      const res = await getReferralWalletSetting();
      if (res.status == 1 && res.data) {
        setSettingForm({
          referral_user_amount: res.data.referral_user_amount ?? "",
          referral_min_w_amount: res.data.referral_min_w_amount ?? ""
        });
      }
    } catch {
      // silent
    } finally {
      setSettingLoading(false);
    }
  };

  const handleSettingChange = (e) => {
    const { name, value } = e.target;
    if (/^\d*\.?\d*$/.test(value) || value === "") {
      setSettingForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSettingSubmit = async () => {
    const userAmt = parseFloat(settingForm.referral_user_amount);
    const minWdAmt = parseFloat(settingForm.referral_min_w_amount);

    if (isNaN(userAmt) || userAmt < 0) {
      notification({ type: "error", message: "Referral commission amount must be a valid positive number" })();
      return;
    }
    if (isNaN(minWdAmt) || minWdAmt < 0) {
      notification({ type: "error", message: "Minimum withdrawal amount must be a valid positive number" })();
      return;
    }

    setSettingSaving(true);
    const res = await updateReferralWalletSetting({
      referral_user_amount: userAmt,
      referral_min_w_amount: minWdAmt
    });
    setSettingSaving(false);

    let notify = notification({
      type: res.status == 0 ? "error" : "success",
      message: res.message
    });
    notify();

    if (res.status == 1) {
      setSettingOpen(false);
    }
  };

  const exportHeaders = [
    { label: "Member Name", key: "m_name" },
    { label: "Mobile No", key: "mobile_no" },
    { label: "Total Referrals", key: "total_referral_count" },
    { label: "Successful Referrals", key: "successful_referral_count" },
    { label: "Earned Amount", key: "earned_amount" },
    { label: "Total Withdrawn", key: "total_withdrawn" },
    { label: "Withdrawable Amount", key: "withdrawable_amount" },
    { label: "Pending Amount", key: "pending_amount" }
  ];

  const exportData = data.map((row) => ({
    m_name: row.m_name || "",
    mobile_no: row.mobile_no || "",
    total_referral_count: row.total_referral_count || 0,
    successful_referral_count: row.successful_referral_count || 0,
    earned_amount: row.earned_amount || 0,
    total_withdrawn: row.total_withdrawn || 0,
    withdrawable_amount: row.withdrawable_amount || 0,
    pending_amount: row.pending_amount || 0
  }));

  const totalPages = Math.ceil(totalRecords / perPage);

  return (
    <Page title="Member Referral Report | Local Vocal Business Group">
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="h4" gutterBottom>
              Member Referral Report
            </Typography>
            {totalRecords > 0 && (
              <Chip
                label={`Total: ${totalRecords}`}
                size="small"
                sx={{
                  bgcolor: "secondary.container",
                  color: "text.primary",
                  fontWeight: 600
                }}
              />
            )}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Settings />}
              onClick={handleOpenSetting}
              sx={{ textTransform: "none" }}
            >
              Referral Setting
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchData}
              disabled={loading}
              sx={{ textTransform: "none" }}
            >
              Refresh
            </Button>
            {data.length > 0 && (
              <Button
                variant="contained"
                disabled={loading}
                startIcon={<FileDownload />}
                sx={{ textTransform: "none" }}
              >
                <CSVLink
                  data={exportData}
                  headers={exportHeaders}
                  filename="Member Referral Report.csv"
                  style={{ textDecoration: "none", color: "inherit" }}
                  target="_blank"
                >
                  Export CSV
                </CSVLink>
              </Button>
            )}
          </Stack>
        </Stack>

        {/* Filters */}
        <Card sx={{ p: 2.5, mb: 3 }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search Member"
                placeholder="Name or Member ID"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Search sx={{ color: "text.secondary", mr: 1, fontSize: 18 }} />
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Withdraw Status</InputLabel>
                <Select
                  value={withdrawFilter}
                  label="Withdraw Status"
                  onChange={(e) => setWithdrawFilter(e.target.value)}
                >
                  {withdrawOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                label="From Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                label="To Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleFilter}
                disabled={loading}
                sx={{ height: 40, textTransform: "none" }}
              >
                Filter
              </Button>
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleClearFilters}
                disabled={loading}
                sx={{ height: 40, textTransform: "none" }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Card>

        {/* Card Grid */}
        <Card sx={{ overflow: "hidden" }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CustomLoader />
            </Box>
          ) : data.length === 0 ? (
            <Box py={8} textAlign="center">
              <Typography variant="body1" color="text.secondary">
                No referral data found
              </Typography>
            </Box>
          ) : (
            <>
              <Grid container spacing={2} sx={{ p: 2.5 }}>
                {data.map((row) => (
                  <Grid item xs={12} sm={6} lg={4} key={row.m_id}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        border: "1px solid",
                        borderColor: "divider",
                        boxShadow: "sm",
                        "&:hover": { boxShadow: 4 },
                        transition: "box-shadow 0.2s",
                        position: "relative"
                      }}
                    >
                      {row?.is_withdrawal_pending === 1 && (
                        <Tooltip title="View Pending Withdrawal Request" arrow>
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/referral-report/${row.m_id}?tab=wallet`);
                            }}
                            sx={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              bgcolor: "warning.light",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              zIndex: 1,
                              cursor: "pointer",
                              "&:hover": { bgcolor: "warning.main" }
                            }}
                          >
                            <PendingActions sx={{ fontSize: 18, color: "warning.dark" }} />
                          </Box>
                        </Tooltip>
                      )}
                      {/* Card Header */}
                      <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ position: "relative", display: "inline-block" }}>
                          <Avatar
                            src={row?.photo ? `${Routes.API_HOSTNAME}images/member/${row.photo}` : undefined}
                            sx={{ width: 40, height: 40, bgcolor: "primary.main", fontSize: 14, fontWeight: 700 }}
                          >
                            {getInitials(row.m_name)}
                          </Avatar>
                          {(row?.m_verify_status === 1 || row?.m_verify_status === 2 || row?.m_verify_status === 3) && (
                            <Box
                              component="img"
                              src={row?.m_verify_status === 1 ? "/static/icons/verified.png" : row?.m_verify_status === 2 ? "/static/icons/verified_blue.png" : "/static/icons/verified_orange.png"}
                              alt="Verified"
                              sx={{ width: 16, height: 16, position: "absolute", top: -1, right: -1 }}
                            />
                          )}
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{row.m_name}</Typography>
                          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                            {row.city_name && (
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <LocationOn sx={{ fontSize: 12, color: "text.secondary" }} />
                                <Typography variant="caption" color="text.secondary">
                                  {row.city_name}
                                </Typography>
                              </Stack>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {row.mobile_no}
                            </Typography>
                          </Stack>
                        </Box>
                      </Box>

                      {/* Stats Row */}
                      <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", textAlign: "center" }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase" }}>Total</Typography>
                          <Typography variant="subtitle1" fontWeight={600}>{row.total_referral_count || 0}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase" }}>Success</Typography>
                          <Typography variant="subtitle1" fontWeight={600} color="success.main">{row.successful_referral_count || 0}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase" }}>Earned</Typography>
                          <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(row.earned_amount)}</Typography>
                        </Box>
                      </Box>

                      {/* Details */}
                      <Box sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Withdrawn</Typography>
                          <Typography variant="body2" fontWeight={500}>{formatCurrency(row.total_withdrawn)}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Withdrawable</Typography>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color={parseFloat(row.withdrawable_amount) > 0 ? "success.main" : "text.secondary"}
                          >
                            {formatCurrency(row.withdrawable_amount)}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Pending Referral Amount</Typography>
                          <Typography variant="body2" fontWeight={500}>{formatCurrency(row.pending_amount)}</Typography>
                        </Box>
                      </Box>

                      {/* View Button */}
                      <Box sx={{ p: 2, pt: 0 }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={() => handleViewDetail(row.m_id)}
                          sx={{ textTransform: "none", fontWeight: 600 }}
                        >
                          View Detail
                        </Button>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Pagination */}
              <Box sx={{ px: 2.5, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: "1px solid", borderColor: "divider" }}>
                <Typography variant="caption" color="text.secondary">
                  Showing <strong>{(page - 1) * perPage + 1}-{Math.min(page * perPage, totalRecords)}</strong> of <strong>{totalRecords}</strong>
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    size="small"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    sx={{ minWidth: 36, p: 0.5 }}
                  >
                    <ChevronLeft fontSize="small" />
                  </Button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        size="small"
                        variant={pageNum === page ? "contained" : "outlined"}
                        onClick={() => setPage(pageNum)}
                        sx={{ minWidth: 36, p: 0.5, fontSize: 12, fontWeight: 600 }}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  <Button
                    size="small"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    sx={{ minWidth: 36, p: 0.5 }}
                  >
                    <ChevronRight fontSize="small" />
                  </Button>
                </Stack>
              </Box>
            </>
          )}
        </Card>

        {/* Referral Setting Modal */}
        <Modal
          open={settingOpen}
          onClose={() => !settingSaving && setSettingOpen(false)}
          slotProps={{ backdrop: { sx: { backdropFilter: "blur(8px)" } } }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 480,
              bgcolor: "background.paper",
              borderRadius: "16px",
              boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
              border: "1px solid",
              borderColor: "divider"
            }}
          >
            <Box sx={{ p: 4 }}>
              <Typography variant="h5" fontWeight={700} mb={2}>
                Referral Setting
              </Typography>
              {settingLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    autoComplete="off"
                    type="text"
                    label="Referral Commission Amount (₹)"
                    name="referral_user_amount"
                    value={settingForm.referral_user_amount}
                    onChange={handleSettingChange}
                    placeholder="e.g. 200"
                    disabled={settingSaving}
                    size="small"
                  />
                  <TextField
                    fullWidth
                    autoComplete="off"
                    type="text"
                    label="Minimum Withdrawal Amount (₹)"
                    name="referral_min_w_amount"
                    value={settingForm.referral_min_w_amount}
                    onChange={handleSettingChange}
                    placeholder="e.g. 1000"
                    disabled={settingSaving}
                    size="small"
                  />
                </Stack>
              )}
            </Box>
            <Stack direction="row" spacing={2} sx={{ p: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setSettingOpen(false)}
                disabled={settingSaving}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSettingSubmit}
                disabled={settingSaving || settingLoading}
                startIcon={settingSaving && <CircularProgress size={18} color="inherit" />}
              >
                Update Settings
              </Button>
            </Stack>
          </Box>
        </Modal>
      </Container>
    </Page>
  );
}
